import { RAIResponse } from "@/app/(protected)/generate-lifestyle/components/StepTwo/components/InputList";
import { ASSET_TYPES, VALID_GTM_EVENT_NAMES } from "@/constants/constants";
import AnalyticsUtil from "./AnalyticsUtils";
import { CategoryMapping, raiCategoryMapping } from "./utils";
import { CommonEventParameters, RAI_STATUS } from "@/types";

export const defaultParams = {
  brandName: "",
  pageName: "",
  userFlow: "",
  stepName: "",
  linkText: "",
  linkSection: "",
  errorMessage: "",
  errorType: "",
  errorLocation: "",
  imageId: "",
  paginationNumber: "",
  timeTaken: "",
  overlayName: "",
  fileStatus: "",
  assetType: "",
  raiStatus: "",
  fileType: "",
  filename: "",
  violations: "",
  thumbs: "",
  channelName: "",
  copyOutput: "",
  imageType: "",
  dimensionName: "",
  imageNumber: "",
  videoLengthName: "",
  videoScriptImageIncluded: "",
  startCreatingAssets: "",
  specificationName: "",
  videoScriptLength: "",
  translationName: "",
  selectionName: "",
  descriptionType: "",
  imageVariance: "",
  noOfAssetsGenerated: "",
  addImage_firstframe: "",
  addImage_lastframe: "",
  aspectRatio: "",
  videoLength: "",
  resolution: "",
  additionalFeedback: "",
  feedbackDetails: "",
  fileAttached: "",
  copyText: "",
};

const getErrorMessages = (raiResponse: RAIResponse): string[] | undefined =>
  raiResponse?.complianceIds?.map((rai) => `${rai}: ${raiCategoryMapping[rai as keyof CategoryMapping]}`);

export const buildEventParams = (baseParams: CommonEventParameters, raiResponse: RAIResponse): CommonEventParameters => {
  const errorMessages = getErrorMessages(raiResponse);
  const violations = raiResponse?.status === RAI_STATUS.FAIL ? (errorMessages ?? raiResponse?.errorMessage) : "";
  const assetType = [raiResponse?.assetType, raiResponse?.status].filter(Boolean).join(" - ");
  return {
    ...baseParams,
    assetType,
    raiStatus: raiResponse?.status,
    violations: violations,
  };
};

export const handleGAOverlayEvent = (eventParams: CommonEventParameters) => {
  AnalyticsUtil.triggerEvent(VALID_GTM_EVENT_NAMES.EVENT_OVERLAY, {
    ...defaultParams,
    ...eventParams,
  });
};

const getRaiErrorMsg = (key: RAIResponse) => {
  const errorMessages = key?.complianceIds?.map((rai) => `${rai}: ${raiCategoryMapping[rai as keyof CategoryMapping]}`);
  return key?.status === RAI_STATUS.FAIL ? { violations: errorMessages ?? key?.errorMessage } : {};
};

// rai check for generated outputs
export const handleGARaiOnGenOutTrigger = (
  generatedOutputRai: {
    description: RAIResponse | null;
    image: RAIResponse | null;
    videoScript: RAIResponse | null;
  },
  pageName: string,
  stepName: string,
  brandName?: string,
) => {
  const { description, image, videoScript } = generatedOutputRai;

  if (description) {
    handleEventTriggerWithParams(VALID_GTM_EVENT_NAMES.RAI_OUTPUT_CHECK, {
      pageName: pageName,
      assetType: `${ASSET_TYPES.TEXT} - ${description.status}`,
      raiStatus: description.status,
      ...getRaiErrorMsg(description),
      brandName: brandName,
      stepName: stepName,
    });
  }

  if (image) {
    handleEventTriggerWithParams(VALID_GTM_EVENT_NAMES.RAI_OUTPUT_CHECK, {
      pageName: pageName,
      assetType: `${ASSET_TYPES.IMAGE} - ${image.status}`,
      raiStatus: image.status,
      ...getRaiErrorMsg(image),
      brandName: brandName,
      stepName: stepName,
    });
  }

  if (videoScript) {
    handleEventTriggerWithParams(VALID_GTM_EVENT_NAMES.RAI_OUTPUT_CHECK, {
      pageName: pageName,
      assetType: `${ASSET_TYPES.VIDEO} - ${videoScript.status}`,
      raiStatus: videoScript.status,
      ...getRaiErrorMsg(videoScript),
      brandName: brandName,
      stepName: stepName,
    });
  }
};

export const handleEventTriggerWithPageName = (eventName: string, pageName: string) => {
  if (eventName !== "" && pageName !== "") {
    AnalyticsUtil.triggerEvent(eventName, {
      pageName: pageName,
    });
  }
  return null;
};

export const handleEventTriggerWithParams = (eventName: string, eventParams: object) => {
  if (eventName !== "") {
    AnalyticsUtil.triggerEvent(eventName, {
      ...eventParams,
    });
  }
  return null;
};

export const handleEventTriggerWithDefaultParams = (eventName: string, eventParams: object) => {
  if (eventName) {
    AnalyticsUtil.triggerEvent(eventName, {
      ...eventParams,
    });
  }
  return null;
};

export const handleGAEventTrigger = (eventName: string, eventParams: CommonEventParameters) => {
  if (eventName) {
    const filteredParams = {
      ...defaultParams,
      ...eventParams,
    };
    if (filteredParams?.pageName) {
      AnalyticsUtil.triggerEvent(eventName, {
        ...filteredParams,
      });
    }
  }
};

export const getUploadEvent = (
  linkText: string,
  linkSection: string,
  additionalData?: Partial<CommonEventParameters>,
): CommonEventParameters => {
  return {
    linkText,
    linkSection,
    ...additionalData,
  };
};
